import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import {
  RAILWAY_WIDTHS,
  RAILWAY_COLOR,
  RAILWAY_SLEEPER_COLOR,
  RAILWAY_SLEEPER_SPACING,
} from '../constants.js';
import { buildQuadStrip, projectAndDeduplicate, segDir } from './roads.js';

/**
 * Build a merged railway mesh (rail bed + wooden sleepers).
 *
 * @param {Array}    railways  Parsed railway objects: { id, railType, bridge, layer, nodes }
 * @param {Object}   proj      Projection helper
 * @param {Function} elevFn    Optional: elevFn(x, z) → metres above scene centre
 * @returns {THREE.Group}
 */
export function buildRailwayMesh(railways, proj, elevFn = null) {
  const railGeos    = [];
  const sleeperGeos = [];

  for (const rwy of railways) {
    const pts = projectAndDeduplicate(rwy.nodes, proj);
    if (pts.length < 2) continue;

    const halfW = RAILWAY_WIDTHS[rwy.railType] ?? RAILWAY_WIDTHS.default;

    // Bridges: lift rail above terrain
    let rwyElev = elevFn;
    if (rwy.bridge) {
      const lift = 6 + Math.max(0, (rwy.layer - 1)) * 4;
      rwyElev = (x, z) => (elevFn ? elevFn(x, z) : 0) + lift;
    }

    // Rail bed strip
    const geo = buildQuadStrip(pts, halfW, rwyElev);
    if (geo) railGeos.push(geo);

    // Sleepers: perpendicular planks every RAILWAY_SLEEPER_SPACING metres
    const sGeos = _buildSleepers(pts, halfW + 0.4, rwyElev);
    sleeperGeos.push(...sGeos);
  }

  const group = new THREE.Group();

  if (railGeos.length > 0) {
    const merged = mergeGeometries(railGeos, false);
    railGeos.forEach(g => g.dispose());
    const mat  = new THREE.MeshLambertMaterial({ color: RAILWAY_COLOR });
    const mesh = new THREE.Mesh(merged, mat);
    mesh.receiveShadow = true;
    mesh.position.y    = 0.06; // slightly above terrain to avoid z-fighting
    group.add(mesh);
  }

  if (sleeperGeos.length > 0) {
    const merged = mergeGeometries(sleeperGeos, false);
    sleeperGeos.forEach(g => g.dispose());
    const mat  = new THREE.MeshLambertMaterial({ color: RAILWAY_SLEEPER_COLOR });
    const mesh = new THREE.Mesh(merged, mat);
    mesh.receiveShadow = true;
    mesh.position.y    = 0.04;
    group.add(mesh);
  }

  return group;
}

// ── Sleeper generation ────────────────────────────────────────────────────────

/**
 * Walk a polyline and emit short perpendicular quad strips every SPACING metres.
 * Each sleeper is a 0.45m-long strip across the full track width.
 */
function _buildSleepers(pts, halfW, elevFn) {
  const geos = [];
  let accum  = 0; // accumulated distance since last sleeper

  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i], b = pts[i + 1];
    const dx = b.x - a.x, dz = b.z - a.z;
    const segLen = Math.sqrt(dx * dx + dz * dz);
    if (segLen < 0.01) continue;

    const nx = dz / segLen, nz = -dx / segLen; // perpendicular normal (right side)

    let dist = accum === 0 ? 0 : RAILWAY_SLEEPER_SPACING - accum;
    while (dist <= segLen) {
      const t  = dist / segLen;
      const cx = a.x + dx * t;
      const cz = a.z + dz * t;
      // Two-point strip: a single perpendicular plank
      const sleeperPts = [
        { x: cx - nx * halfW, z: cz - nz * halfW },
        { x: cx + nx * halfW, z: cz + nz * halfW },
      ];
      const geo = buildQuadStrip(sleeperPts, 0.22, elevFn); // 0.44m plank width
      if (geo) geos.push(geo);
      dist += RAILWAY_SLEEPER_SPACING;
    }

    accum = segLen - (dist - RAILWAY_SLEEPER_SPACING);
  }

  return geos;
}
